/**
 * Enable parts of an element to be replaced with newly-rendered content from Section Rendering API
 *
 * Requirements:
 * - Dynamic elements MUST have a data-section-id attribute set on the custom element in Liquid
 * - `reRenderSection()` is designed to work with a single custom element within a section.
 *   If there are multiple of the same element in a section it wont work as expected.
 *   The first element with the data-section-id attribute will be used.
 */

import morphdom from 'morphdom';

class DynamicSection extends HTMLElement {
  connectedCallback() {
    this.setSectionRenderLinkListeners();
  }

  /**
   * Default options for morphdom
   * @type {Object}
   *
   * @notes see https://github.com/patrick-steele-idem/morphdom?tab=readme-ov-file#morphdomfromnode-tonode-options--node
   */
  defaultMorphdomOptions = {
    onBeforeElUpdated: fromElement => {
      // If the fromElement has a data-no-morph attribute, don't update it
      if (Object.hasOwn(fromElement.dataset, 'noMorph')) {
        return false;
      }
    },

    onBeforeNodeDiscarded: node => {
      if (!(node instanceof HTMLElement)) {
        return;
      }
      // If the node has a data-no-morph attribute, don't delete it
      if (Object.hasOwn(node.dataset, 'noMorph')) {
        return false;
      }
    },

    onBeforeElChildrenUpdated: fromElement => {
      // If the fromEl has a data-no-morph attribute, don't update its children
      if (Object.hasOwn(fromElement.dataset, 'noMorph')) {
        return false;
      }
    }
  };

  /**
   *  Return the id of the section this custom element is rendered in
   */
  get sectionId() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      /* eslint-disable-next-line no-console */
      console.error(
        new Error('data-section-id attribute not set on custom element')
      );
    }
    return sectionId;
  }

  /**
   * If a click in the section is on an anchor tag with a data-section-render-link attribute
   * The link is section rendered instead of navigating to the URL
   * @returns {void}
   */
  setSectionRenderLinkListeners() {
    this.addEventListener('click', event => {
      const link = event.target.closest('a[data-section-render-link]');
      if (link) {
        event.preventDefault();
        const scrollTo = link.dataset.scrollTo ?? undefined;
        this.reRenderSection({
          url: link.href,
          replaceState: true,
          scrollTo,
          beforeReplaceMethod: this.beforeReplaceMethod.bind(this)
        });
      }
    });
  }

  /**
   * @param {Object} options
   * @param {String} options.url - the URL to fetch the updated section from
   * @param {Boolean} options.replaceState - whether to replace the current URL in the browser history
   * @param {Array.<String>} options.updateOnly - an array of query selectors in this section to update
   * @param {Function} options.beforeReplaceMethod - a method to run before replacing the section. Is passed the newSectionElement and should return it.
   * @param {'section_top' | undefined} options.scrollTo - scrolls the user to the top of the section after the section is re-rendered
   * @param {String} options.sectionName - the name of the section to render. Optional. If not provided, we use this.sectionId.
   * @param {String} options.sectionNameToReplace - the name of the section to replace. Optional, If not provided, we replace the section of the same name as the custom element.
   */
  reRenderSection(options = {}) {
    let url = options.url ?? '';
    const sectionIdentifier = options.sectionName ?? this.sectionId;
    // Load new HTML for this module's section from Section Rendering API

    const symbol = url.includes('?') ? '&' : '?';

    let request_url = `${url}${symbol}sections=${sectionIdentifier}`;

    return fetch(request_url)
      .then(response => response.json())
      .then(responseJson => {
        const newSectionElement = new DOMParser().parseFromString(
          responseJson[sectionIdentifier],
          'text/html'
        );

        if (options.beforeReplaceMethod) {
          options.beforeReplaceMethod(newSectionElement);
        }

        this.replaceContent(newSectionElement, options);

        if (options.afterReplaceMethod) {
          options.afterReplaceMethod();
        }
      });
  }

  /**
   * Re-render multiple sections in the same request
   * @param {Array{String}} sections - An array of section IDs to update
   * @param {String} options.url - the URL to fetch the updated section from
   * @param {Boolean} options.replaceState - whether to replace the current URL in the browser history
   * @param {Array.<String>} options.updateOnly - an array of query selectors in this section to update
   * @param {Function} options.beforeReplaceMethod - a method to run before replacing the section. Is passed the newSectionElement and should return it.
   * @param {'section_top' | undefined} options.scrollTo - scrolls the user to the top of the section after the section is re-rendered
   *
   * @returns {Promise{Response}}
   */
  static reRenderMultipleSections(sectionIds = [], options = {}) {
    if (!sectionIds || sectionIds.length === 0) return;

    const url = options.url ?? '';
    // Load new HTML for this module's section from Section Rendering API
    const symbol = url.includes('?') ? '&' : '?';
    return fetch(`${url}${symbol}sections=${sectionIds.join(',')}`)
      .then(response => response.json())
      .then(responseJson => {
        for (const sectionId of sectionIds) {
          const newSectionElement = new DOMParser().parseFromString(
            responseJson[sectionId],
            'text/html'
          );

          if (options.beforeReplaceMethod) {
            // Apply any manipulations to newSectionElement before replacing the current section
            options.beforeReplaceMethod(newSectionElement);
          }

          const targetElement = document.querySelector(
            `[data-section-id="${sectionId}"]`
          );

          if (targetElement) {
            targetElement.replaceContent(newSectionElement, options);
          }
        }
      });
  }

  /**
   * Replace content inside current element
   * @param {HTMLElement} newSectionElement - The updated HTML Element for this section returned by the Section Rendering API
   * @param {Object} options
   */
  replaceContent(newSectionElement, options = {}) {
    const {
      url,
      replaceState,
      updateOnly,
      morphdomOptions,
      sectionNameToReplace
    } = options;

    this.morphdomOptions = morphdomOptions
      ? {
          ...this.defaultMorphdomOptions,
          ...morphdomOptions
        }
      : this.defaultMorphdomOptions;

    const sectionIdentifier =
      sectionNameToReplace || `[data-section-id="${this.sectionId}"]`;
    const newCustomElement = newSectionElement.querySelector(sectionIdentifier);

    if (!newCustomElement) return;

    // If we've defined an array of selectors to update, iterate through them and do those targeted updates
    if (updateOnly && updateOnly.length > 0) {
      for (const selector of updateOnly) {
        const newElement = newCustomElement.querySelector(selector);
        const currentElement = this.querySelector(selector);
        if (newElement && currentElement) {
          currentElement.replaceWith(newElement);
        }
      }
    } else {
      morphdom(this, newCustomElement, this.morphdomOptions);
    }

    if (replaceState && url != '') {
      globalThis.history.replaceState(undefined, undefined, url);
    }

    this.afterSectionRender(options);
  }

  /**
   * Updates the contents of all the sections passed in
   * @param {object} sectionsCartResponse - From the shopify API, the sections object is a key-value pair of section id and html string
   * @param {Object} options
   * @returns {void}
   */
  static replaceMultipleSectionsContent(sectionsCartResponse, options = {}) {
    for (const [sectionId, htmlString] of Object.entries(
      sectionsCartResponse
    )) {
      const newSectionElement = new DOMParser().parseFromString(
        htmlString,
        'text/html'
      );

      /**
       * @type {HTMLElement}
       * @augments {DynamicSection}
       */
      const targetElement = document.querySelector(
        `[data-section-id="${sectionId}"]`
      );
      if (targetElement) {
        targetElement.replaceContent(newSectionElement, options);
      }
    }
  }

  /**
   * @param {string} options.scrollTo - where to scroll to after the section is re-rendered
   * @notes Override this method in child classes to run code after the section has been updated.
   * If you wish to keep the default behavior as well, call `super.afterSectionRender(options)`
   */
  afterSectionRender({ scrollTo } = {}) {
    if (!scrollTo) return;

    if (scrollTo === 'section_top') {
      /* If the top of the section is out of view, scroll to it */
      const section = this.closest('.shopify-section');
      if (section.offsetTop < window.scrollY) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}

customElements.get('dynamic-section') ||
  customElements.define('dynamic-section', DynamicSection);

export default DynamicSection;
